<script setup>
import { computed, onMounted } from "vue";
import { BubbleMenu } from "@tiptap/vue-3/menus";
import TurnIntoDropdown from "./TurnIntoDropdown.vue";
import LinkPopover from "./LinkPopover.vue";
import ColorTextPopover from "./ColorTextPopover.vue";
import { useEditorMenuState } from "../../composables/useEditorMenuState";

const props = defineProps({
  editor: { type: Object, required: true },
  defaultParticipantCode: { type: String, default: null },
});

const menuState = useEditorMenuState();

const activeBlockLabel = computed(() => {
  const e = props.editor;
  if (!e) return "Текст";
  if (e.isActive("heading", { level: 1 })) return "Заголовок 1";
  if (e.isActive("heading", { level: 2 })) return "Заголовок 2";
  if (e.isActive("heading", { level: 3 })) return "Заголовок 3";
  if (e.isActive("bulletList")) return "Список";
  if (e.isActive("orderedList")) return "Нум. список";
  if (e.isActive("blockquote")) return "Цитата";
  if (e.isActive("codeBlock")) return "Код";
  return "Текст";
});

const bubbleOptions = {
  strategy: "fixed",
  placement: "top",
  offset: 8,
  flip: { fallbackPlacements: ["bottom", "top-start", "bottom-start"] },
  shift: { padding: 8 },
  hide: true,
};

const appendTo = () => document.body;

onMounted(() => {
  const shell = props.editor?.view?.dom?.closest(".editor-shell");
  if (shell) bubbleOptions.scrollTarget = shell;
});

function shouldShow({ editor, state }) {
  if (!editor?.isEditable) return false;
  if (menuState.blockMenuOpen) return false;
  return !state.selection.empty;
}

function cmd(name, args) {
  if (args) {
    props.editor.chain().focus()[name](args).run();
  } else {
    props.editor.chain().focus()[name]().run();
  }
}
</script>

<template>
  <BubbleMenu
    :editor="editor"
    plugin-key="selectionToolbar"
    :should-show="shouldShow"
    :update-delay="100"
    :options="bubbleOptions"
    :append-to="appendTo"
    class="selection-bubble"
    :class="{ 'is-suppressed': menuState.blockMenuOpen }"
  >
    <div class="selection-toolbar" @mousedown.prevent>
      <TurnIntoDropdown
        :editor="editor"
        :default-participant-code="defaultParticipantCode"
      >
        <button class="sel-btn sel-btn--text">
          <div class="text-[13px] font-medium">{{ activeBlockLabel }}</div>
          <svg
            class="w-2.5 h-2.5 ml-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </TurnIntoDropdown>

      <div class="sel-divider" />

      <button
        class="sel-btn"
        :class="{ active: editor.isActive('bold') }"
        @click="cmd('toggleBold')"
        title="Bold"
      >
        <div class="font-bold text-sm">B</div>
      </button>

      <button
        class="sel-btn"
        :class="{ active: editor.isActive('italic') }"
        @click="cmd('toggleItalic')"
        title="Italic"
      >
        <div class="italic text-sm">I</div>
      </button>

      <button
        class="sel-btn"
        :class="{ active: editor.isActive('underline') }"
        @click="cmd('toggleUnderline')"
        title="Underline"
      >
        <div class="underline text-sm">U</div>
      </button>

      <button
        class="sel-btn"
        :class="{ active: editor.isActive('strike') }"
        @click="cmd('toggleStrike')"
        title="Strike"
      >
        <div class="line-through text-sm">S</div>
      </button>

      <div class="sel-divider" />

      <button
        class="sel-btn"
        :class="{ active: editor.isActive('code') }"
        @click="cmd('toggleCode')"
        title="Code"
      >
        <svg
          class="w-[15px] h-[15px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      </button>

      <LinkPopover :editor="editor" @mousedown.stop>
        <button
          class="sel-btn"
          :class="{ active: editor.isActive('link') }"
          title="Link"
        >
          <svg
            class="w-[15px] h-[15px]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path
              d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            />
            <path
              d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            />
          </svg>
        </button>
      </LinkPopover>

      <ColorTextPopover :editor="editor" @mousedown.stop>
        <button class="sel-btn" title="Color">
          <div class="text-sm font-medium">A</div>
          <svg
            class="w-2.5 h-2.5 ml-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </ColorTextPopover>

      <div class="sel-divider" />

      <button class="sel-btn" title="Delete" @click="cmd('deleteSelection')">
        <svg
          class="w-[15px] h-[15px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path
            d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
          />
        </svg>
      </button>
    </div>
  </BubbleMenu>
</template>

<style scoped>
.selection-bubble {
  z-index: 50;
  transition: opacity 120ms ease;
}

.selection-bubble.is-suppressed {
  display: none !important;
}

.selection-toolbar {
  @apply flex items-center gap-1;
  @apply bg-popover border border-border;
  padding: 4px 6px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  pointer-events: auto;
}

.sel-btn {
  @apply w-9 h-9 flex items-center justify-center rounded-md;
  @apply text-muted-foreground;
  @apply transition-all duration-100;
}

.sel-btn:hover {
  @apply bg-muted text-foreground;
}

.sel-btn.active {
  @apply bg-muted text-foreground;
}

.sel-btn--text {
  @apply px-2.5 w-auto gap-0.5;
}

.sel-divider {
  @apply w-px h-6 bg-border mx-1;
}
</style>
